import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { GestationalAge } from "@/Patient/Models/ObstetricalFile/GestationalAge";

class ObstetricsCalculator {
    private static readonly gestationLengthInDays = 280;

    /**
     * Calculates the date of birth based on the last Menstrual Period.
     * @param lastMenstrualPeriod the date of last menstrual period (DLMP).
     * @param daysInCycle The number of days in a cycle.
     * @return the date of birth.
     */
    public static calculateDateOfBirthBasedOnLastMenstrualPeriod(lastMenstrualPeriod: Date, daysInCycle?: number): Date {
        daysInCycle ??= 28;
        return LuxonDateTimeParser.parse(lastMenstrualPeriod)?.plus({ days: daysInCycle - 14 }).plus({ weeks: 38 }).toJSDate();
    }

    /**
     * Calculates the date of birth based on the ultrasound
     * @param ultrasoundDate the date of the ultrasound to use.
     * @param ultrasoundAge the Gestational Age of the ultrasound.
     * @return the date of birth.
     */
    public static calculateDateOfBirthBasedOnUltrasound(ultrasoundDate: Date, ultrasoundAgeInDays: number): Date {
        return LuxonDateTimeParser.parse(ultrasoundDate)?.plus({ days: this.gestationLengthInDays }).minus({ days: ultrasoundAgeInDays }).toJSDate();
    }

    /**
     * Calculates the weeks of gestation based on EDC and consultation date
     * @param estimatedDateOfBirth the estimated date of confinement.
     * @param consultationDate the consultation date.
     * @return Gestational Age.
     */
    public static calculateWeeksOfGestation(estimatedDateOfBirth: Date, consultationDate: Date): GestationalAge {
        const parsedEstimatedDateOfBirth = DateOnly.parseUnlessNull(estimatedDateOfBirth)?.toLuxon();
        const parsedConsultationDate = DateOnly.parseUnlessNull(consultationDate)?.toLuxon();

        const dateDiff: number = parsedEstimatedDateOfBirth && parsedConsultationDate ?
            parsedEstimatedDateOfBirth.diff(parsedConsultationDate, ["days"]).days : null;
        const gestationDays = dateDiff != null ? (this.gestationLengthInDays - dateDiff) : null;

        const daysInOneWeek = 7;
        const weeks = gestationDays ? Math.floor(gestationDays / daysInOneWeek) : null;
        const days = gestationDays ? gestationDays - (weeks * daysInOneWeek) : null;

        return new GestationalAge({
            NumberOfWeeks: weeks?.toString() ?? "",
            NumberOfDays: days?.toString() ?? ""
        });
    }

    /**
     * Calculates the number of weeks postpartum based on consultation and delivery dates.
     * @param deliveryDate date at which the baby is born.
     * @param consultationDate the consultation date.
     * @return Number of weeks postpartum.
     */
    public static calculateNumberOfWeeksPostpartum(deliveryDate: Date, consultationDate: Date): number {
        const parsedDeliveryDate = DateOnly.parseUnlessNull(deliveryDate)?.toLuxon();
        const parsedConsultationDate = DateOnly.parseUnlessNull(consultationDate)?.toLuxon();

        return parsedConsultationDate?.diff(parsedDeliveryDate, ["weeks", "days"]).weeks;
    }

    /**
     * Calculates the age of the patient at estimated date of birth.
     * @param patientDateOfBirth date at which the patient is born.
     * @param estimatedDateOfBirth the estimated date of birth.
     * @return Age at estimated date of birth.
     */
    public static calculateAgeAtEstimatedDateOfBirth(patientDateOfBirth: Date, estimatedDateOfBirth: Date): number {
        const parsedPatientDateOfBirth = DateOnly.parseUnlessNull(patientDateOfBirth)?.toLuxon();
        const parsedEstimatedDateOfBirth = DateOnly.parseUnlessNull(estimatedDateOfBirth)?.toLuxon();

        if (!parsedPatientDateOfBirth || !parsedEstimatedDateOfBirth)
            return null;

        return Math.floor(Math.abs(parsedEstimatedDateOfBirth.diff(parsedPatientDateOfBirth, "years").years));
    }
}

export { ObstetricsCalculator };
